import React, { useEffect, useRef } from 'react'

/**
 * Fires `onImpress` exactly once, when the element first becomes ~50% visible in the viewport.
 * Used for analytics impression events. Safe when IntersectionObserver is unavailable (no-op).
 *
 * Optional `onExit(dwellMs)` fires once when the element first LEAVES the viewport (or on
 * unmount if it was still visible), reporting how long it was visible. Lower-risk than mutating
 * the entry event: the existing impression fires unchanged, dwell is an additive exit signal.
 */
export function useImpression<T extends HTMLElement>(
  onImpress: () => void,
  onExit?: (dwellMs: number) => void,
): React.RefObject<T> {
  const ref = useRef<T>(null)
  const firedRef = useRef(false)
  const exitedRef = useRef(false)
  const visibleSinceRef = useRef<number | null>(null)
  // Keep the latest callbacks without re-subscribing the observer on every render.
  const callbackRef = useRef(onImpress)
  callbackRef.current = onImpress
  const exitCallbackRef = useRef(onExit)
  exitCallbackRef.current = onExit

  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      return
    }

    const emitExit = (): void => {
      if (exitedRef.current || visibleSinceRef.current == null) {
        return
      }
      exitedRef.current = true
      const dwellMs = Date.now() - visibleSinceRef.current
      visibleSinceRef.current = null
      exitCallbackRef.current?.(dwellMs)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!firedRef.current) {
              firedRef.current = true
              visibleSinceRef.current = Date.now()
              callbackRef.current()
            }
          } else {
            // Element left the viewport — emit dwell (only after it was seen).
            emitExit()
          }
        })
      },
      { threshold: 0.5 },
    )
    observer.observe(el)
    return () => {
      // Element still visible at unmount — count that as an exit too.
      emitExit()
      observer.disconnect()
    }
  }, [])

  return ref
}
